import classnames from "classnames";
import * as React from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { toastr } from "react-redux-toastr";
import { useDataLoader } from "react-remote-data-hooks";
import { useHistory } from "react-router-dom";
import { DropdownList } from "react-widgets";
import api from "../../api";
import { ContainerLoader } from "../../components/loader";
import Config from "../../config";
import {
  groupProjects, htmlDecode, ILeadActivity, ILeadEvent, IOfficeLocation, IUpdateEventModel, MediaFile, setProposalTabStatus, setShortListPropertyData, State, transformProjectsData
} from "../../core";
import { PropertyDetails, Shortlist } from "../../core/api/models/project";
import { ProposalReason } from "../../core/api/models/proposal";
import {
  LeadRequirement,
  RequirementStatus,
} from "../../core/api/models/requirement";
import { useLeadActivities } from "../../core/hooks/useLeadActivities";
import "../../styles/full-view-popup.scss";
import "../../styles/requirement.scss";
import "../../styles/sales-person.scss";
import "../../styles/tags.scss";
import { ActivityModel } from "../leads/components/activities/add-activity";
import { EditEventPopup } from "../leads/components/activities/edit-event-popup";
import { RouteKeys } from "../routes/route-keys";
import CaptureGoogleMapView from "../send-proposal/components/CaptureGoogleMapView";
import { FormattedData } from "../send-proposal/components/EditProposedCommercials";
import ProposalPreview from "../send-proposal/components/proposal-preview";
import { AccordionTab } from "../send-proposal/components/props";
import SortProperties from "../send-proposal/components/SortProperties";
import FileUploader from "./components/FileUploader";
import GalleryList from "./components/GalleryList";
import PropertyListTab from "./components/PropertyListTab";
import { FurnishingDetailsViewOption, TabNames } from "./components/props";
import SelectedProperties from "./components/SelectedProperties";
import Tabs from "./components/Tabs";
import { CreateProposalCoverGalleryPopup } from "./create-proposal-cover-gallery-popup";
import { CreateProposalFormItems } from "./create-proposal-form-items";
import { CreateProposalGalleryPopup } from "./create-proposal-gallery-popup";
import { CreateProposalHeader } from "./create-proposal-header";
import { CreateProposalProjectGalleryPopup } from "./create-proposal-project-gallery-popup";
import { CreateFormProps, IConfigurationProps, ProjectApiImages, ProjectImage } from "./props";

const CreateProposal: React.FunctionComponent<IConfigurationProps> = (
  props
) => {
  const history = useHistory();

  const {
    getEventDetails,
    createEventUpdateFormData,
    updateActivity,
  } = useLeadActivities(api.lead);

  const [canCompareVersion, setCanCompareVersion] = React.useState(false);
  const [tabName, setTabName] = React.useState<TabNames>(TabNames.Properties);
  const [accordionTabName, setOpen] = React.useState<
    AccordionTab | undefined
  >();
  const [selectedProperties, setSelectedProperties] = React.useState<
    PropertyDetails[]
  >();
  const [selectedExcludedFloor, setSelectedExcludedFloor] = React.useState<
    PropertyDetails[]
  >();
  const [selectedExcludedPhotoGallery, setSelectedExcludedPhotoGallery] = React.useState<
    PropertyDetails[]
  >();
  const shortListedPropertyData = useSelector(
    (state: State) => state.shortListPoperty.shortListPropertyArray
  );
  const [isOnEdit, setIsOnEdit] = React.useState(false);
  const [proposalKey, setNewProposalKey] = React.useState(new Date().getTime());
  const [showPreview, setShowPreview] = React.useState(false);
  const [createData, setCreateData] = React.useState<any>(undefined);
  const [currentActivity, setCurrentActivity] = React.useState<ActivityModel>();
  const [isEditing, setIsEditing] = React.useState(false);
  const [galleryPopup, setGalleryPopup] = React.useState(false);
  const [projectGalleryPopup, setProjectGalleryPopup] = React.useState<{
    open: boolean;
    projectDetails?: {
      resImages?: any[];
      projectName?: string;
      _id?: string;
      imagePath?: string;
    } | null;
  }>({ open: false, projectDetails: null });
  const [coverGalleryPopup, setCoverGalleryPopup] = React.useState(false);
  const [selectedImages, setSelectedImages] = React.useState<any[]>([]);
  const [selectedCoverImages, setCoverSelectedImages] = React.useState<any[]>([]);
  const [selectedProjectImages, setProjectSelectedImages] = React.useState<ProjectImage[]>([]);
  const [selectedExcludedFurnishing, setSelectedExcludedFurnishing] = React.useState<PropertyDetails[]>([]);
  const [selectedFloorplanFurnishing, setSelectedFloorplanFurnishing] = React.useState<{ [key: string]: string }>({});
  // Add state for tracking which projects should show third column
  const [showThirdColumn, setShowThirdColumn] = React.useState<{ [key: string]: boolean }>({});
  const [officeLocation, setOfficeLocation] = React.useState<IOfficeLocation & { name: string, lat: number | undefined, lng: number | undefined, isOfficeLocation: boolean } | undefined>();
  const [editedProposedPropertyData, setEditedProposedPropertyData] = React.useState<FormattedData[]>([]);
  const [isEdited, setIsEdited] = React.useState(false)

  React.useEffect(() => {
    if (!isEditing) {
      setCurrentActivity(undefined);
    }
  }, [isEditing]);
  const onPressEdit = async (act: ILeadActivity) => {
    if (act.event != null) {
      getEventDetails(act?.event?._id, (eventDetails: ILeadEvent) => {
        const activityModel: ActivityModel = createEventUpdateFormData(
          eventDetails
        );
        setCurrentActivity(activityModel);
        setIsEditing(true);
      });
    } else {
      toastr.error("Error", "Event is null");
    }
  };

  const dispatch = useDispatch();

  const {
    handleSubmit,
    errors,
    control,
    watch,
    setValue,
    formState: { isDirty },
  } = useForm<CreateFormProps>({
    shouldFocusError: true,
    shouldUnregister: true,
    reValidateMode: "onChange",
  });

  React.useEffect(() => {
    if (isDirty) {
      setIsEdited(true)
    }

  }, [isDirty])

  React.useEffect(() => {
    if (
      props?.selectedShortListedProperty &&
      props?.selectedShortListedProperty.length
    ) {
      setSelectedProperties(props?.selectedShortListedProperty);
    }
    if (shortListedPropertyData && shortListedPropertyData.length) {
      setSelectedProperties(shortListedPropertyData);
      dispatch(setProposalTabStatus(true));
    }
  }, [props?.selectedShortListedProperty, shortListedPropertyData]);

  React.useEffect(() => {
    if (props?.proposalId) {
      loadProposal();
    }
  }, [props?.proposalId]);

  const loadProposal = async () => {
    const proposal = await api.proposal.findProposalById(props?.proposalId!);

    if (proposal) {
      const createProposalData = {
        requirementId: props.requirementId,
        versionNumber: requirementData?.versionNumber,
        status: "draft",
        createdAt: proposal.createdAt,
        excludeFloorPlan: !!proposal.excludeFloorPlan,
        sendProposalReason: proposal.sendProposalReason,
        furnishingDetailsViewOption: proposal.furnishingDetailsViewOption,
        propertyIds: proposal?.properties?.map(
          (x) => x.propertyDetails?.propertyId
        ),
        activityDetails: proposal?.activityDetails,
        sendProposalComment: proposal.sendProposalComment,
        proposalFormatType: proposal.proposalFormatType,
        proposalContentType: proposal.proposalContentType,
        capturedLocationsImage: proposal.capturedLocationsImage,
        tagFrom:
          proposal.activityDetails?.type === "note" ? "note" : "activity",
      };

      setCreateData(createProposalData);

      setValue("reason", proposal.sendProposalReason);
      setValue("comment", proposal.sendProposalComment);
      setValue("activity", proposal.activityDetails);
      setValue("excludeFloorPlan", proposal.excludeFloorPlan);
      setValue("proposalFormatType", proposal.proposalFormatType);
      setValue("proposalContentType", proposal.proposalContentType);
      setValue("tagFrom", createProposalData?.tagFrom);
      setCoverSelectedImages(proposal.coverImagesMedia)
      setProjectSelectedImages(transformProjectsData(proposal.projectImages))
      setSelectedImages(proposal.innerImagesMedia)
      if (proposal.currentOffice) {
        setOfficeLocation({ ...proposal.currentOffice, name: proposal.currentOffice?.building || '', lat: proposal.currentOffice?.latitude, lng: proposal.currentOffice?.longitude, isOfficeLocation: true })
      }

      setIsOnEdit(true);
      setShowPreview(false);
      setOpen(AccordionTab.ProposalForm);
      setSelectedProperties(
        proposal?.properties?.map((x) => {
          return {
            ...x.propertyDetails?.propertyDetailsFromProject,
            latitude: x.propertyDetails?.propertyLatitude,
            longitude: x.propertyDetails?.propertyLongitude,
            cityName: x?.propertyDetails?.city?.cityName,
            cityLocName: x?.propertyDetails?.cityLocation?.cityLocName,

          };
        })
      );
      if (props?.title === "Edit Proposal") {
        const tempSltExFlr = proposal?.properties?.filter(
          (exFlrItems) => exFlrItems?.excludeFloorPlan === true
        );
        if (tempSltExFlr && tempSltExFlr?.length > 0) {
          setSelectedExcludedFloor(
            tempSltExFlr?.map((x) => {
              return {
                ...x.propertyDetails?.propertyDetailsFromProject,
                latitude: x.propertyDetails?.propertyLatitude,
                longitude: x.propertyDetails?.propertyLongitude,
              };
            })
          );
        }
        const tempSltExPhoto = proposal?.properties?.filter(
          (exPhotoItems) => exPhotoItems?.excludePhotoGallery === true
        );
        if (tempSltExPhoto && tempSltExPhoto?.length > 0) {
          setSelectedExcludedPhotoGallery(tempSltExPhoto?.map((x) => {
            return {
              ...x.propertyDetails?.propertyDetailsFromProject,
              latitude: x.propertyDetails?.propertyLatitude,
              longitude: x.propertyDetails?.propertyLongitude,
            };
          }))

        }
        const tempSltExFurnishing = proposal?.properties?.filter(
          (exFurnishingItems) => exFurnishingItems?.excludeFurnishingDetails === true
        );
        if (tempSltExFurnishing && tempSltExFurnishing?.length > 0) {
          setSelectedExcludedFurnishing(tempSltExFurnishing?.map((x) => {
            return {
              ...x.propertyDetails?.propertyDetailsFromProject,
              latitude: x.propertyDetails?.propertyLatitude,
              longitude: x.propertyDetails?.propertyLongitude,
            };
          }))
        }

        // Load floorplan furnishing details
        const floorplanFurnishingDetails = proposal?.properties?.reduce((acc, property) => ({
          ...acc,
          [property.propertyDetails?.propertyDetailsFromProject?.propertyId]: property.furnishingDetailsViewOption
        }), {});

        setSelectedFloorplanFurnishing(floorplanFurnishingDetails || {});
      }

      setTabName(TabNames.SelectedProperties);
    }
  };

  const [isSaving, setSaving] = React.useState(false);

  const [isUpdatingActivity, setUpdatingActivity] = React.useState(false);

  const {
    data: sortList,
    reload: reloadShortList,
    loading,
  } = useDataLoader<Shortlist>(() =>
    api.shortlist.getShortList(props.requirementId)
  );

  const { data: versionList } = useDataLoader<LeadRequirement[]>(() =>
    api.requirement.getRequirementVersions(props.requirementId)
  );

  const { data: reasons } = useDataLoader<ProposalReason[]>(() =>
    api.proposal.loadAllProposalReason({ entityType: "ProposalCreate" })
  );

  const { data: requirementData } = useDataLoader<LeadRequirement>(() =>
    api.requirement.findRequirementById(props.requirementId)
  );

  const { data: proposalCoverImages, reload } = useDataLoader<any[]>(() =>
    api.proposal.loadAllProposalCoverImages()
  );

  const { data: proposalProjectImages, reload: reloadProjectImages, setData: setProjectImages } = useDataLoader<ProjectApiImages[]>((projectIds: string[]) => {
    api.proposal.loadAllProjectImages(projectIds || []).then(res => {
      setProjectImages(res)
    })
  }
  )

  React.useEffect(() => {
    const projectIds = selectedProperties?.map(x => x.projectId)
    if (projectIds?.length) {
      reloadProjectImages(projectIds);
      setProjectSelectedImages(prev => prev.filter(img => selectedProperties?.some(prop => prop.projectId === img?.projectId)))
    }
  }, [selectedProperties])

  const handleTabChange = (tabNameValue: TabNames) => {
    setTabName(tabNameValue);
  };
  const [imageCaptured, setImageCaptued] = React.useState<MediaFile>();

  const activity =
    (watch("activity") as ILeadActivity) || createData?.activityDetails;

  const createProposal = async () => {
    const newProperties = selectedProperties?.filter((x) => x.isNew);

    setSaving(true);
    if (!!newProperties?.length) {
      await api.shortlist.createShortList({
        requirementId: props.requirementId!,
        propertyDetails: selectedProperties!,
      });
    }
    const createProposalData = {
      requirementId: props.requirementId,
      versionNumber: requirementData.versionNumber,
      status: "draft",
      // excludeFloorPlan: createData.excludeFloorPlan,
      furnishingDetailsViewOption: createData.furnishingDetailsViewOption,
      excludeFloorPlanIds: createData.excludeFloorPlanIds,
      excludePhotoGalleryIds: createData.excludePhotoGalleryIds,
      excludeFurnishingDetailIds: createData.excludeFurnishingDetailsIds,
      furnishingDetailOptionViews: createData.floorplanFurnishingView,
      sendProposalReason: createData.sendProposalReason,
      propertyIds: createData?.propertyIds,
      linkedActivity: createData?.activityDetails?._id,
      sendProposalComment: createData.sendProposalComment,
      proposalFormatType: createData.proposalFormatType,
      proposalContentType: createData.proposalContentType,
      isSendProposal: true,
      capturedLocationsImage: imageCaptured,
      coverImages: selectedCoverImages?.[0]?.media?.location ? [selectedCoverImages?.[0]?.media?.location] : [],
      innerImages: selectedImages?.map((x) => x?.media?.location),
      coverImagesMedia: selectedCoverImages.filter(x => x),
      innerImagesMedia: selectedImages.filter(x => x),
      currentOffice: officeLocation,
      proposalCommercialDetails: editedProposedPropertyData,
      projectImages: groupProjects(selectedProjectImages),
    };
    if (props.proposalId) {
      await api.proposal.updateProposal(createProposalData, props.proposalId);
      toastr.success("Success", "Proposal successfully updated");
    } else {
      await api.proposal.createProposal(createProposalData);
      toastr.success("Success", "Proposal successfully created");
    }

    setSaving(false);
    setSelectedProperties([]);
    props.reload?.();

    if (props.isRedirect) {
      history.push(
        `${RouteKeys.Proposal.replace(
          ":id",
          requirementData.masterRequirementId || requirementData._id
        )}`
      );
    } else {
      props.onClose?.();
      dispatch(setShortListPropertyData([]));
    }
  };
  const createPreview = async (data: any) => {
    const createProposalData = {
      requirementId: props.requirementId,
      versionNumber: requirementData?.versionNumber,
      status: "draft",
      createdAt: createData?.createdAt,
      excludeFloorPlan: !!data.excludeFloorPlan,
      excludeFloorPlanIds: selectedExcludedFloor?.map((x) => x?.propertyId) || [],
      excludePhotoGalleryIds: selectedExcludedPhotoGallery?.map((x) => x?.propertyId) || [],
      floorplanFurnishingView: transformFurnishingView(selectedFloorplanFurnishing),
      furnishingDetailsViewOption: data.furnishingDetailsViewOption || createData?.furnishingDetailsViewOption,
      sendProposalReason: data.reason || createData?.sendProposalReason,
      propertyIds: selectedProperties?.map((x) => x.propertyId),
      activityDetails: data?.activity || createData?.activityDetails,
      sendProposalComment: data.comment || createData?.sendProposalComment,
      capturedLocationImage: imageCaptured || createData?.capturedLocationsImage,
      proposalFormatType:
        data.proposalFormatType || createData?.proposalFormatType,
      proposalContentType:
        data.proposalContentType || createData?.proposalContentType,
      tagFrom: data.tagFrom,
      coverImages: selectedCoverImages?.[0]?.media?.location ? [selectedCoverImages?.[0]?.media?.location] : [],
      innerImages: selectedImages?.map((x) => x?.media?.location),
      coverImagesMedia: selectedCoverImages.filter(x => x),
      innerImagesMedia: selectedImages.filter(x => x),
      excludeFurnishingDetailsIds: selectedExcludedFurnishing?.map((x) => x?.propertyId) || [],
      currentOffice: officeLocation,
    };
    setCreateData(createProposalData);
    setShowPreview(true);
  };

  const onSubmitActivity = async (apiModel: IUpdateEventModel) => {
    setUpdatingActivity(true);
    updateActivity(currentActivity?.eventId!, apiModel, async () => {
      const response = await api.lead.getActivityDetails(activity?._id!);
      setValue("activity", response?.data);
      setIsEditing(false);
      setUpdatingActivity(false);
    });
  };

  const uniqueProjects = new Set();
  let projectCounter = 0;

  const locations: any = selectedProperties
    ?.map((x) => {
      if (!uniqueProjects.has(x.projectId)) {
        uniqueProjects.add(x.projectId);
        projectCounter += 1;

        return {
          lat: parseFloat((x.latitude || "")?.toString()) || 0,
          lng: parseFloat((x.longitude || "")?.toString()) || 0,
          name: projectCounter?.toString() || "",
          address: x.propertyName || '',
          propertyAddress: x.propertyAddress || '',
        };
      }
      return null; // Return null for duplicate projects
    })
    .filter(Boolean);

  const handleFloorplanFurnishingUpdate = (propertyId: string, value: string) => {
    setIsEdited(true);
    setSelectedFloorplanFurnishing(prev => ({
      ...prev,
      [propertyId]: value
    }));
  };

  const transformFurnishingView = (data: Record<string, string>) => {
    return Object.entries(data).map(([propertyId, viewOption]) => ({
      propertyId,
      furnishingDetailsViewOption: viewOption || FurnishingDetailsViewOption.TwoColumn
    }));
  };

  return !showPreview ? (
    <>
      <div
        className={`detail-poupup  position-fixed top-0 w-100 h-100 overflow-auto in `}
        style={{ zIndex: 1051 }}
      >
        <CreateProposalHeader
          setShortListPropertyData={setShortListPropertyData}
          title={props.title}
          onClose={props?.onClose}
          canCompareVersion={canCompareVersion}
          dispatch={dispatch}
          setCanCompareVersion={setCanCompareVersion}
        />
        {loading && !sortList?.shortListedProperties?.length ? (
          <div className="px-4 bg-white pt-3  mx-4 my-4 border pb-2">
            <ContainerLoader></ContainerLoader>
          </div>
        ) : (
          <div
            className={classnames({
              "px-4 bg-white pt-3  mx-4 my-2 border ": true,
              "pb-3 ":
                accordionTabName === AccordionTab.Properties ||
                accordionTabName === AccordionTab.SelectPropery ||
                undefined,
              "pb-3": !(
                accordionTabName === AccordionTab.Properties ||
                accordionTabName === AccordionTab.SelectPropery ||
                undefined
              ),
            })}
          >
            <div className="d-flex align-items-center justify-content-between">
              <div>
                {" "}
                <label className="font-md font-weight-semibold pt-1 pb-2">
                  Select Property
                </label>
              </div>
              {tabName === TabNames.SelectedProperties &&
                !!selectedProperties?.length && (
                  <div className="font-sm font-weight-bold text-gray-500 d-flex align-items-center">
                    Selected ({selectedProperties?.length || 0})
                    <div className="ml-2">
                      <i
                        className={classnames({
                          "float-right cursor-pointer": true,
                          "icon-chevron-up":
                            accordionTabName === AccordionTab.SelectPropery,
                          "icon-chevron-down":
                            accordionTabName !== AccordionTab.SelectPropery,
                        })}
                        onClick={() =>
                          setOpen(
                            accordionTabName === AccordionTab.SelectPropery
                              ? undefined
                              : AccordionTab.SelectPropery
                          )
                        }
                      ></i>
                    </div>
                  </div>
                )}
            </div>

            {(accordionTabName === undefined ||
              accordionTabName === AccordionTab.Properties ||
              accordionTabName === AccordionTab.SelectPropery) && (
                <>
                  <div className="property-tab">
                    <Tabs
                      tabName={tabName}
                      tabChangeHandler={handleTabChange}
                      selectedProperties={selectedProperties}
                    />
                  </div>
                  {tabName === TabNames.Properties && (
                    <PropertyListTab
                      showThirdColumn={showThirdColumn}
                      setShowThirdColumn={setShowThirdColumn}
                      requirementId={props.requirementId}
                      sortList={sortList}
                      reloadShortList={() => reloadShortList(props.requirementId)}
                      versionList={versionList?.filter(
                        (x) => x.status === RequirementStatus.Approved
                      )}
                      onSelect={(propertys: PropertyDetails[]) => {
                        setIsEdited(true);
                        const currentSelectedProperties =
                          selectedProperties || [];
                        const uniquePropertys = propertys.filter(
                          (property) =>
                            !currentSelectedProperties.some(
                              (selectedProperty) =>
                                selectedProperty.propertyId ===
                                property.propertyId
                            )
                        );

                        setSelectedProperties([
                          ...(selectedProperties || []),
                          ...(uniquePropertys || []),
                        ]);
                        setTabName(TabNames.SelectedProperties);
                        setOpen(AccordionTab.SelectPropery);
                      }}
                      onSelectExcludeFloor={(exFlr: PropertyDetails[]) => {
                        setSelectedExcludedFloor(exFlr);
                      }}
                      onSelectExcludePhotoGallery={(exPhoto: PropertyDetails[]) => {
                        setSelectedExcludedPhotoGallery(exPhoto);
                      }}
                      onSelectExcludeFurnishing={(exFurnishing: PropertyDetails[]) => {
                        setSelectedExcludedFurnishing(exFurnishing);
                      }}
                      excludeFloorDetails={selectedExcludedFloor || []}
                      excludePhotoDetails={selectedExcludedPhotoGallery || []}
                      excludeFurnishingDetails={selectedExcludedFurnishing || []}
                      selectedProperties={selectedProperties!}
                      proposedProperties={props.proposedProperties}
                      isSelectedShortListedProperty={
                        props?.selectedShortListedProperty?.length ? true : false
                      }
                      reloadRequirement={props.reloadRequirement}
                      selectedFloorplanFurnishing={selectedFloorplanFurnishing}
                      onUpdateFloorplanFurnishing={handleFloorplanFurnishingUpdate}
                    ></PropertyListTab>
                  )}
                  {tabName === TabNames.SelectedProperties && (
                    <SelectedProperties
                      showThirdColumn={showThirdColumn}
                      setShowThirdColumn={setShowThirdColumn}
                      requirementId={props.requirementId}
                      sortList={sortList?.shortListedProperties}
                      onRemove={(propertyIds: PropertyDetails[]) => {
                        setIsEdited(true);
                        setSelectedProperties([...(propertyIds || [])]);
                      }}
                      onRemoveExFlr={(propertyIds: PropertyDetails[]) => {
                        setIsEdited(true);
                        setSelectedExcludedFloor([...(propertyIds || [])]);
                      }}
                      onRemoveExPhoto={(propertyIds: PropertyDetails[]) => {
                        setIsEdited(true);
                        setSelectedExcludedPhotoGallery([...(propertyIds || [])]);
                      }}
                      onRemoveExFurnishing={(propertyIds: PropertyDetails[]) => {
                        setIsEdited(true);
                        setSelectedExcludedFurnishing([...(propertyIds || [])]);
                      }}
                      onAddExFlr={(propertyIds: PropertyDetails[]) => {
                        setIsEdited(true);
                        setSelectedExcludedFloor([...(propertyIds || [])]);
                      }}
                      onAddExPhoto={(propertyIds: PropertyDetails[]) => {
                        setIsEdited(true);
                        setSelectedExcludedPhotoGallery([...(propertyIds || [])]);
                      }}
                      onAddExFurnishing={(propertyIds: PropertyDetails[]) => {
                        setIsEdited(true);
                        setSelectedExcludedFurnishing([...(propertyIds || [])]);
                      }}
                      propertyDetails={selectedProperties!}
                      excludeFloorDetails={selectedExcludedFloor!}
                      excludePhotoDetails={selectedExcludedPhotoGallery!}
                      excludeFurnishingDetails={selectedExcludedFurnishing!}
                      onSelect={() => {
                        setOpen(AccordionTab.SortProperties);
                      }}
                      selectedFloorplanFurnishing={selectedFloorplanFurnishing}
                      onUpdateFloorplanFurnishing={handleFloorplanFurnishingUpdate}
                    ></SelectedProperties>
                  )}
                </>
              )}
          </div>
        )}

        {!!selectedProperties?.length && (
          <>
            <div
              className={classnames({
                "px-4 bg-white pt-3 mx-4 my-2 border": true,
                " pb-3": accordionTabName !== AccordionTab.SortProperties,
                "pb-4": accordionTabName === AccordionTab.SortProperties,
              })}
            >
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <label className="font-md font-weight-semibold mb-0">
                    Sort Property
                  </label>
                </div>
                <div className="font-sm font-weight-bold text-gray-500 d-flex align-items-center">
                  <div className="ml-2">
                    <i
                      className={classnames({
                        "float-right cursor-pointer": true,
                        "icon-chevron-up":
                          accordionTabName === AccordionTab.SortProperties,
                        "icon-chevron-down":
                          accordionTabName !== AccordionTab.SortProperties,
                      })}
                      onClick={() =>
                        setOpen(
                          accordionTabName === AccordionTab.SortProperties
                            ? undefined
                            : AccordionTab.SortProperties
                        )
                      }
                    ></i>
                  </div>
                </div>
              </div>

              {accordionTabName === AccordionTab.SortProperties && (
                <>
                  <SortProperties
                    requirementId={props.requirementId}
                    proposedProperties={props.proposedProperties!}
                    onRemove={(propertyIds: PropertyDetails[]) => {
                      setIsEdited(true);
                      setSelectedProperties([...(propertyIds || [])]);
                    }}
                    setSelectedProperties={(data) => {
                      setIsEdited(true);
                      setSelectedProperties(data)
                    }}
                    propertyDetails={selectedProperties!}
                    onSelect={() => {
                      setOpen(AccordionTab.LocationMap);
                    }}
                  />
                </>
              )}
            </div>

            <div
              className={classnames({
                "px-4 bg-white pt-3  mx-4 my-2 border": true,
                "pb-3": accordionTabName !== AccordionTab.LocationMap,
                "pb-4": accordionTabName === AccordionTab.LocationMap,
              })}
            >
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <label className="font-md font-weight-semibold mb-0">
                    Location Map
                  </label>
                </div>
                <div className="font-sm font-weight-bold text-gray-500 d-flex align-items-center">
                  <div className="ml-2">
                    <i
                      className={classnames({
                        "float-right cursor-pointer": true,
                        "icon-chevron-up":
                          accordionTabName === AccordionTab.LocationMap,
                        "icon-chevron-down":
                          accordionTabName !== AccordionTab.LocationMap,
                      })}
                      onClick={() =>
                        setOpen(
                          accordionTabName === AccordionTab.LocationMap
                            ? undefined
                            : AccordionTab.LocationMap
                        )
                      }
                    ></i>
                  </div>
                </div>
              </div>
              {accordionTabName === AccordionTab.LocationMap && (
                <div className="d-flex w-100">
                  <div className="w-75">
                    <CaptureGoogleMapView
                      apiKey={Config.SITE_VISIT_GOOGLE_MAP_API_KEY}
                      locations={officeLocation ? [...locations, officeLocation]! : locations!}
                      onContinue={(image?: MediaFile) => {
                        setIsEdited(true);
                        setImageCaptued(image);
                        setOpen(AccordionTab.GalleryPopup);
                      }}
                    ></CaptureGoogleMapView>
                  </div>

                  <div className="map-view-pointer w-25 py-4">
                    <div className="pl-3 mb-4">
                      <h5 className="">Current Office Location</h5>
                      <div className="d-flex align-items-center">
                        <DropdownList
                          data={requirementData?.companyDetails?.
                            offices?.map(x => ({ ...x, name: x.building })) || []}
                          textField={"name"}
                          // valueField={"value"}
                          placeholder="Select"
                          onChange={(val) => setOfficeLocation({ ...val, name: val?.building || '', lat: val?.latitude, lng: val?.longitude, address: val?.address || '', isOfficeLocation: true })}
                          value={officeLocation}
                        />
                        <span className="text-danger ml-1" onClick={() => setOfficeLocation(undefined)}>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 26 26"
                            fill="none"
                          >
                            <path
                              d="M25.0416 0.292893C24.6511 -0.0976308 24.0179 -0.0976312 23.6274 0.292893L12.6673 11.253L1.70711 0.292893C1.31658 -0.0976312 0.683417 -0.0976308 0.292893 0.292893C-0.0976308 0.683417 -0.0976312 1.31658 0.292893 1.70711L11.253 12.6673L0.292893 23.6274C-0.0976308 24.0179 -0.0976308 24.6511 0.292893 25.0416C0.683417 25.4322 1.31658 25.4322 1.70711 25.0416L12.6673 14.0815L23.6274 25.0416C24.0179 25.4322 24.6511 25.4322 25.0416 25.0416C25.4322 24.6511 25.4322 24.0179 25.0416 23.6274L14.0815 12.6673L25.0416 1.70711C25.4322 1.31658 25.4322 0.683417 25.0416 0.292893Z"
                              fill="#6F7171"
                            />
                          </svg>
                        </span>
                      </div>
                    </div>
                    <div>
                      <h5 className="pl-3">Proposed Property Locations</h5>
                      <table className="table w-100">
                        {Array.from(
                          new Set(
                            selectedProperties?.map((item) => item?.projectId)
                          )
                        )?.map((projectId, index) => {
                          const item = selectedProperties?.find(
                            (property) => property?.projectId === projectId
                          );
                          return (
                            <>
                              <tr className="require-row">
                                <td
                                  className="p-2 border-bottom font-sm"
                                  style={{ maxWidth: "30px", width: "30px" }}
                                >
                                  <div className="py-1">
                                    <label>{index + 1}</label>
                                  </div>
                                </td>
                                <td
                                  className="py-2 border-bottom font-sm"
                                  style={{ width: "60px", maxWidth: "60px" }}
                                >
                                  <div className="py-1 cursor-pointer">
                                    <img
                                      src={item?.propertyCoverImage}
                                      alt={item?.projectName}
                                      width={59}
                                      height={45}
                                      className="img-cover"
                                    />
                                  </div>
                                </td>
                                <td
                                  className="py-2 border-bottom font-sm"
                                  style={{ width: "280px", maxWidth: "280px" }}
                                >
                                  <div className="py-1 w-100 px-3">
                                    <div className="font-sm text-truncate cursor-pointer">
                                      {item?.projectName || ""}
                                    </div>
                                    <div className="font-xs text-gray-500">
                                      {htmlDecode(item?.propertyAddress || "")}
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            </>
                          );
                        })}
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div
              className={classnames({
                "px-4 bg-white pt-3  mx-4 my-2 border ": true,
                "pb-3": accordionTabName !== AccordionTab.GalleryPopup,
                "pb-5": accordionTabName === AccordionTab.GalleryPopup,
              })}
            >
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <label className="font-md font-weight-semibold mb-0">
                    Image
                  </label>
                </div>
                <div className="font-sm font-weight-bold text-gray-500 d-flex align-items-center">
                  <div className="ml-2">
                    <i
                      className={classnames({
                        "float-right cursor-pointer": true,
                        "icon-chevron-up":
                          accordionTabName === AccordionTab.GalleryPopup,
                        "icon-chevron-down":
                          accordionTabName !== AccordionTab.GalleryPopup,
                      })}
                      onClick={() =>
                        setOpen(
                          accordionTabName === AccordionTab.GalleryPopup
                            ? undefined
                            : AccordionTab.GalleryPopup
                        )
                      }
                    ></i>
                  </div>
                </div>
              </div>

              {accordionTabName === AccordionTab.GalleryPopup && (
                <>
                  <div className="mt-4 pt-1">
                    <h4 className="font-normal font-weight-semibold">
                      Main Image<span className="text-danger">*</span>
                    </h4>

                    <div className="img-uploader-proposal flex-wrap d-flex align-items-center pt-1">
                      {selectedCoverImages.length ? <GalleryList imageUrl={selectedCoverImages?.[0]?.media?.location} imageName={selectedCoverImages?.[0]?.media?.originalname} onDelete={() => {
                        setIsEdited(true);
                        setCoverSelectedImages(prev => prev.filter(img => img?._id !== selectedCoverImages?.[0]?._id))
                      }} /> : null}
                      {selectedCoverImages.length < 1 ? <FileUploader onOpen={() => setCoverGalleryPopup(true)} /> : null}
                    </div>
                  </div>

                  <div className="row">
                    {proposalProjectImages?.map((project, index) => {
                      return <div className="col-md-6" key={index}><div className="mt-4 pt-1">
                        <h4 className="font-normal font-weight-semibold">
                          {project?.projectName}
                        </h4>
                        <>
                          <span className="font-sm font-weight-semibold">
                            Select Images
                          </span>
                          <div className="img-uploader-proposal flex-wrap d-flex align-items-center pt-1">

                            {selectedProjectImages?.filter(img => img.projectId === project?._id)?.map((image, indx) => {
                              return <GalleryList key={indx} imageUrl={image.projectImage} imageName={image.projectImageName} onDelete={() => {
                                setIsEdited(true);
                                setProjectSelectedImages(prev => prev.filter(img => +img.projectImageID !== +image.projectImageID))
                              }} />;
                            })}
                            <FileUploader onOpen={() => setProjectGalleryPopup({ open: true, projectDetails: project })} />
                          </div>
                        </>
                      </div>
                      </div>
                    })}
                  </div>

                  <div className="d-flex justify-content-end">
                    <button
                      type="button"
                      className="btn bg-primary select-prop-bt font-normal font-weight-bold text-white"
                      disabled={!selectedCoverImages.length}
                      onClick={() => {
                        setOpen(AccordionTab.ProposalForm);
                      }}
                    >
                      Continue
                    </button>
                  </div>
                  {projectGalleryPopup.open && (
                    <CreateProposalProjectGalleryPopup
                      projectGalleryPopup={projectGalleryPopup}
                      title={props?.title}
                      selectedProjectImages={selectedProjectImages}
                      setIsEdited={setIsEdited}
                      setProjectGalleryPopup={setProjectGalleryPopup}
                      setProjectSelectedImages={setProjectSelectedImages}
                    />
                  )}

                  {galleryPopup && (
                    <CreateProposalGalleryPopup
                      proposalCoverImages={proposalCoverImages}
                      title={props?.title}
                      selectedImages={selectedImages}
                      setGalleryPopup={setGalleryPopup}
                      setSelectedImages={setSelectedImages}
                    />
                  )}
                  {coverGalleryPopup && (
                    <CreateProposalCoverGalleryPopup
                      title={props?.title}
                      proposalCoverImages={proposalCoverImages}
                      requirementData={requirementData}
                      selectedCoverImages={selectedCoverImages}
                      setCoverGalleryPopup={setCoverGalleryPopup}
                      setCoverSelectedImages={setCoverSelectedImages}
                    />
                  )}

                </>
              )}
            </div>

            <div
              className={classnames({
                "px-4 bg-white pt-3  mx-4 my-2 border ": true,
                "pb-3": accordionTabName !== AccordionTab.ProposalForm,
                "pb-5": accordionTabName === AccordionTab.ProposalForm,
              })}
            >
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <label className="font-md font-weight-semibold mb-0">
                    Proposal Format, Reason & Comments to the Approver
                  </label>
                </div>
                <div className="font-sm font-weight-bold text-gray-500 d-flex align-items-center">
                  <div className="ml-2">
                    <i
                      className={classnames({
                        "float-right cursor-pointer": true,
                        "icon-chevron-up":
                          accordionTabName === AccordionTab.ProposalForm,
                        "icon-chevron-down":
                          accordionTabName !== AccordionTab.ProposalForm,
                      })}
                      onClick={() =>
                        setOpen(
                          accordionTabName === AccordionTab.ProposalForm
                            ? undefined
                            : AccordionTab.ProposalForm
                        )
                      }
                    ></i>
                  </div>
                </div>
              </div>

              {<div className={accordionTabName === AccordionTab.ProposalForm ? 'd-block' : 'd-none'}>
                <CreateProposalFormItems
                  activity={activity}
                  control={control}
                  createData={createData}
                  errors={errors}
                  isUpdatingActivity={isUpdatingActivity}
                  onPressEdit={onPressEdit}
                  reasons={reasons}
                  requirementData={requirementData}
                  setValue={setValue}
                  watch={watch}
                />
              </div>}
            </div>
          </>
        )}
        <div className="d-flex justify-content-end px-4 mb-5 ">
          <button
            type="button"
            className="btn select-prop-bt btn-primary text-white font-weight-semibold "
            onClick={handleSubmit(createPreview)}
            disabled={
              ((accordionTabName !== AccordionTab.ProposalForm ||
                !watch("proposalFormatType") ||
                !watch("proposalContentType") ||
                !watch("reason")) &&
                !isOnEdit) || selectedCoverImages.length < 1
            }
          >
            Preview
          </button>
        </div>

        {isEditing && currentActivity !== undefined && (
          <EditEventPopup
            isEdit={true}
            currentActivity={currentActivity}
            onClose={() => {
              setIsEditing(false);
            }}
            onSubmit={(updatedEvent) => {
              onSubmitActivity(updatedEvent);
            }}
            leadId={requirementData?.leadDetails?._id!}
          ></EditEventPopup>
        )}
      </div>

      <div className="modal-backdrop fade show" style={{ zIndex: 1050 }}></div>
    </>
  ) : (
    <ProposalPreview
      key={proposalKey}
      isEdited={isEdited}
      proposalId={props.proposalId}
      proposalIdForPreview={props.proposalId}
      requirementId={props.requirementId}
      createData={createData}
      requirementData={requirementData}
      selectedProperties={selectedProperties!}
      selectedExcludedFloorIds={
        selectedExcludedFloor && selectedExcludedFloor?.length > 0
          ? selectedExcludedFloor?.map((item) => item?.propertyId || "")
          : []
      }
      selectedExcludedPhotoGalleryIds={
        selectedExcludedPhotoGallery && selectedExcludedPhotoGallery?.length > 0
          ? selectedExcludedPhotoGallery?.map((item) => item?.propertyId || "")
          : []
      }
      selectedExcludedFurnishingIds={
        selectedExcludedFurnishing && selectedExcludedFurnishing?.length > 0
          ? selectedExcludedFurnishing?.map((item) => item?.propertyId || "")
          : []
      }
      floorplanAndFurnishingDetails={transformFurnishingView(selectedFloorplanFurnishing)}
      onEdit={() => {
        setShowPreview(false);
        setIsOnEdit(true);
      }}
      onClose={() => {
        setShowPreview(false);
        setIsOnEdit(true);
      }}
      refreshComponent={() => {
        setNewProposalKey(new Date().getTime());
      }}
      onSend={() => createProposal()}
      isSaving={isSaving}
      editedProposedPropertyData={editedProposedPropertyData}
      setEditedProposedPropertyData={setEditedProposedPropertyData}
      selectedProjectImages={selectedProjectImages}
    ></ProposalPreview>
  );
};

export default CreateProposal;
